export interface GetStartedInput {
  readonly balanceCents?: number;
  readonly keysCount?: number;
  readonly queryCount?: number;
}

export interface GetStartedState {
  readonly funded: boolean;
  readonly hasKey: boolean;
  readonly hasUsage: boolean;
  readonly completedSteps: number;
}

export function deriveGetStartedState(input: GetStartedInput): GetStartedState {
  const funded = (input.balanceCents ?? 0) > 0;
  const hasKey = (input.keysCount ?? 0) > 0;
  const hasUsage = (input.queryCount ?? 0) > 0;
  return {
    funded,
    hasKey,
    hasUsage,
    completedSteps: Number(funded) + Number(hasKey) + Number(hasUsage),
  };
}

export function checkoutBalanceBaseline(search: string): number | null {
  const value = new URLSearchParams(search).get("balance_before");
  if (value === null) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function checkoutOrderId(search: string): string | null {
  const params = new URLSearchParams(search);
  const orderId = params.get("order_id");
  return params.get("topup") === "success" &&
    orderId !== null &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(orderId)
    ? orderId
    : null;
}

export function legacyTopupBalanceIncreased(
  storedBaseline: string | null,
  currentBalanceCents: number | undefined,
): boolean {
  if (storedBaseline === null || !/^\d+$/.test(storedBaseline)) return false;
  const baseline = Number(storedBaseline);
  return Number.isSafeInteger(baseline) &&
    currentBalanceCents !== undefined &&
    currentBalanceCents > baseline;
}

export function stableBrowserIdempotencyKey(storageKey: string): string {
  const existing = window.sessionStorage.getItem(storageKey);
  if (existing) return existing;
  const value = `engine.signup:${crypto.randomUUID()}`;
  window.sessionStorage.setItem(storageKey, value);
  return value;
}

export function topupAttemptStorageKey(amountUsd: number): string {
  return `ando.engine.topup-attempt.${amountUsd}`;
}

export function topupAttemptIdempotencyKey(amountUsd: number): string {
  return stableBrowserIdempotencyKey(topupAttemptStorageKey(amountUsd));
}

export function consumeTopupAttempt(amountUsd: number): void {
  window.sessionStorage.removeItem(topupAttemptStorageKey(amountUsd));
}

export const MIN_CUSTOM_TOPUP_USD = 5;
export const MAX_CUSTOM_TOPUP_USD = 1_000;

export function parseCustomTopupUsd(value: string): number | null {
  if (!/^\d+$/.test(value.trim())) return null;
  const amount = Number(value);
  return Number.isSafeInteger(amount) &&
    amount >= MIN_CUSTOM_TOPUP_USD &&
    amount <= MAX_CUSTOM_TOPUP_USD
    ? amount
    : null;
}
